import { useCallback, useMemo, useState } from "react";
import { useLanguage } from "../../../../stores/LanguageStore";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { useListingLoader } from "../../../../components/hooks/ListingHooks";
import SelectionDeleter from "../../../../components/TableComponents/SelectionDeleter";
import ItemDeleter from "../../../../components/TableComponents/ItemDeleter";
import {
  ActionIcon,
  Anchor,
  Button,
  CopyButton,
  Divider,
  Flex,
  Group,
  HoverCard,
  List,
  ListItem,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import dateColumn from "../../../../layouts/Table/columns/dateColumn";
import { nameColumn } from "../../../../layouts/Table/columns/nameColumn";
import MainTable from "../../../../layouts/Table/MainTable";
import { sharingColumn } from "../../../../layouts/Table/columns/sharingColumn";
import SelectionSharer from "../../../../components/TableComponents/SelectionSharer";
import { IconArrowLeft, IconPlus } from "@tabler/icons-react";
import { deleteField, editField, getFields } from "../../../../api/ContactAPI";
import { Link } from "react-router-dom";
import numberColumn from "../../../../layouts/Table/columns/numberColumn";

const DatafieldsListing = () => {
  const apiRef = useGridApiRef();
  const lang = useLanguage((s) => s.language);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const columns = useMemo(
    () => [
      numberColumn(),
      nameColumn({
        disableLink: true,
        handleClick: () => { },
        editFunction: () => editField,
        getDisabled: () => false,
        apiRef,
      }),
      {
        field: "identifier_placeholder",
        headerName: lang.datafields.columns.identifier_placeholder,
        width: 200,
        renderCell: ({ row, value }) => {
          return (
            <CopyButton value={value}>
              {({ copied, copy }) => (
                <Tooltip
                  label={copied ? "Copié" : "Cliquez pour Copier"}
                  color={copied ? "green" : "black"}
                >
                  <Anchor c={"black"} onClick={copy} truncate="end">
                    {value}
                  </Anchor>
                </Tooltip>
              )}
            </CopyButton>
          );
        },
      },
      {
        field: "possible_values",
        headerName: lang.datafields.create.dialog.possible_values,
        width: 200,
        renderCell: ({ row, value }) => {
          return value?.length === 0 || !value ? (
            "-"
          ) : (
            <HoverCard withArrow offset={0}>
              <HoverCard.Target>
                <Text>
                  <b>{value.length}</b>{" "}
                  {lang.datafields.create.dialog.possible_values}
                </Text>
              </HoverCard.Target>
              <HoverCard.Dropdown>
                <List gap={"sm"}>
                  {value.map((item, i) => (
                    <ListItem key={i}>{item}</ListItem>
                  ))}
                </List>
              </HoverCard.Dropdown>
            </HoverCard>
          );
        },
      },
      dateColumn(),
      sharingColumn({ apiRef, editFunction: () => editField }),
    ],
    [apiRef]
  );

  const actionsCell = useCallback(
    ({ row }) => {
      return (
        <ItemDeleter
          title_bold={`${row.name} #${row.id}`}
          handleDelete={deleteField}
          deletedMessage={lang.tags.delete_success}
          id={row.id}
          apiRef={apiRef}
        />
      );
    },
    [apiRef, lang.tags.delete_success]
  );

  const toolbarActions = useCallback(
    ({ selectedIds }) => (
      <>
        <SelectionDeleter
          handleDelete={deleteField}
          apiRef={apiRef}
          setTableLoading={setLoading}
          ids={selectedIds}
        />

        <SelectionSharer
          handleShare={editField}
          apiRef={apiRef}
          setTableLoading={setLoading}
          ids={selectedIds}
        />
      </>
    ),
    [apiRef]
  );

  useListingLoader({
    loading,
    setLoading,
    listGetter: params => getFields(false),
    responseField: "data_fields_list",
    setRows,
  });

  return (
    <Stack flex={1} pr={48} pt={16} gap={0}>
      <Group gap={"md"}>
        <ActionIcon size={"xl"} component={Link} to={"/settings/tools"}>
          <IconArrowLeft />
        </ActionIcon>
        <Text size="xl" fw={600} mr={"auto"}>
          {lang.settings.tool_datafields}
        </Text>

        <Button
          component={Link}
          to={"create"}
          size="md"
          leftSection={<IconPlus />}
        >
          {lang.datafields.create.dialog.title}
        </Button>
      </Group>

      <Divider my={16} />

      <Flex h={"calc(100vh - 196px)"}>
        <MainTable
          apiRef={apiRef}
          columns={columns}
          rows={rows}
          loading={loading}
          setLoading={setLoading}
          id={"list-table"}
          toolbarActions={toolbarActions}
          actionsCell={actionsCell}
          actionsCellWidth={50}
          whiteButtons
          tableSx={{
            overflow: "hidden",
            "& .MuiDataGrid-pinnedColumns": {
              background: "#fff",
              borderTopLeftRadius: 3,
              border: "none",
            },
            "& .MuiDataGrid-pinnedColumnHeaders": {
              background: "#fff",
              border: "none",
            },
            "& .table-search": {
              width: "300px !important",
            },
          }}
        />
      </Flex>
    </Stack>
  );
};

export default DatafieldsListing;
